<template>
  <div class="data-story" :lang="selectedLanguage">
    <Loader v-if="showLoader"></Loader>

    <div v-show="!showLoader">
      <iframe
        scrolling="no"
        src="about:blank"
        ref="storyHolder"
        :style="storyFrameStyle"
      />

      <div class="data-story-meta" v-show="story.id">
        <div class="symbols">
          <a class="symbol-email" :href="shareEmailUrl" target="_blank"></a>
          <a
            v-show="story.GitHub_URL"
            class="symbol-github"
            target="_blank"
            :href="story.GitHub_URL"
          ></a>
          <a
            v-show="story.DOI"
            class="symbol-doi"
            target="_blank"
            :href="story.DOI"
          ></a>
        </div>
        <div class="tags">
          <a
            class="tag"
            v-for="tagId in story.tagList"
            :key="tagId"
            :href="`/stories?tag=${tagId}`"
          >
            {{ tags.length ? tags.find((t) => t.TagId === tagId).label : tagId }}
          </a>
        </div>

        <div class="authors" v-html="authorsHtml"></div>

        <div class="authors">
          <h3>{{ cmsTranslationByKey("ContactPerson") }}</h3>
          <p>
            <a style="text-decoration: underline" href="mailto:datastories@snf.ch">
              datastories@snf.ch
            </a>
          </p>
        </div>

        <div class="similar-stories" v-show="similarStories.length">
          <h2 class="similar-stories--title">
            {{ cmsTranslationByKey("SubheaderSimilarStories") }}
          </h2>

          <div
            v-for="story in similarStories"
            class="similar-story"
            :key="story.id"
            @click="openSimilarStory(story)"
          >
            <hr />
            <h3 class="story-title">
              <router-link
                :to="{
                  name: 'DataStory',
                  params: {
                    htmlFilename: story.Name,
                  },
                }"
              >
                {{ story.title }}
              </router-link>
            </h3>

            <p class="story-date">
              {{ story.PublicationDate | dateFormat }}
            </p>

            <div class="story-link">
              <router-link
                :to="{
                  name: 'DataStory',
                  params: {
                    htmlFilename: story.Name,
                  },
                }"
              >
                {{ cmsTranslationByKey("ButtonToStory") }}
                {{ readOnLanguagesText(story.Languages) }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import * as log from "loglevel";
import * as _ from "lodash";

import { mapActions, mapGetters } from "vuex";
import { buildEntityQuery, loadDataStories } from "@/solrHelpers";
import { decompressDataStoryData } from "@/utils";
import { cmsTranslationMixin } from "@/mixins";
import Loader from "@/components/Loader";

// show image in story iframe full screen -> function outside of vue
window.showImgFullscreen = function (src) {
  let modal;

  function removeModal() {
    document.body.removeChild(modal);
  }

  modal = document.createElement("div");
  modal.style.background = "RGBA(0,0,0,.5) url(" + src + ") no-repeat center";
  modal.style.backgroundSize = "contain";
  modal.style.width = "100%";
  modal.style.height = "100%";
  modal.style.position = "fixed";
  modal.style.zIndex = "10000";
  modal.style.top = "0";
  modal.style.left = "0";
  modal.style.cursor = "zoom-out";
  modal.addEventListener("click", () => {
    removeModal();
  });
  document.body.appendChild(modal);
};

// all keys
/*
const solrFields = [
  "Author",
  "AuthorURL",
  "Category",
  "Created",
  "DOI",
  "DataStoryCategory",
  "DataStoryName",
  "Entity",
  "GitHub_URL",
  "Language",
  "Languages",
  "Modified",
  "Name",
  "PublicationDate",
  "ShortDescription_De",
  "ShortDescription_En",
  "ShortDescription_Fr",
  "Tags",
  "ThumbnailExtension",
  "Title_De",
  "Title_En",
  "Title_Fr",
  "TopStory",
  "id"
]
 */

const solrFields = [
  "Author",
  "Author_De",
  "Author_Fr",
  "Author_En",
  "AuthorURL",
  "Category",
  "Created",
  "DOI",
  "DataStoryCategory",
  "DataStoryName",
  "Entity",
  "GitHub_URL",
  "Language",
  "Languages",
  "Modified",
  "Name",
  "PublicationDate",
  "Title_De",
  "Title_En",
  "Title_Fr",
  "ShortDescription_De",
  "ShortDescription_En",
  "ShortDescription_Fr",
  "Tags",
  "TopStory",
  "id",

  "FileLink",
  "ThumbnailLink",
];

export default {
  name: "DataStory",
  metaInfo: function () {
    if (this.story.id) {
      let imageEntry = {
        vmid: "og:image",
        property: "og:image",
        content: `${window.location.origin}/img/meta_image_data_stories.png`,
      };

      if (this.story.ThumbnailLink) {
        imageEntry.content = `${window.location.origin}${this.story.ThumbnailLink}`;
      }

      return {
        title: this.objectAttributeInLanguage(this.story, "Title"),
        meta: [
          {
            vmid: "og:title",
            property: "og:title",
            content: this.objectAttributeInLanguage(this.story, "Title"),
          },
          {
            vmid: "og:description",
            property: "og:description",
            content: this.objectAttributeInLanguage(this.story, "ShortDescription"),
          },
          imageEntry,
        ],
      };
    }
    return {};
  },
  // eslint-disable-next-line vue/require-prop-types
  props: ["htmlFilename"],
  mixins: [cmsTranslationMixin],
  components: { Loader },
  data: function () {
    return {
      htmlFileContent: "",
      storyFrameStyle: {
        width: "100%",
        height: "100vh",
      },
      story: {},
      similarStories: [],
      showLoader: false,
    };
  },
  mounted: function () {
    log.debug("DataStory mounted", this.htmlFilename, this.$route.query);

    if (!window.localStorage.getItem("selectedLanguage")) {
      // CF-743 if the user has no stored language then she is here for the first time
      // set language accordingly
      const storyLang = _.split(this.htmlFilename, ".")[0].slice(-2);
      if (["en", "fr"].includes(storyLang)) {
        this.setSelectedLanguage(storyLang);
      }
    }

    this.loadPageData();
    if (!this.dataStoriesTags.length) {
      this.loadDataStoriesTags();
    }
  },
  computed: {
    ...mapGetters(["selectedLanguage", "cmsData", "screenSize", "dataStoriesTags"]),

    tags: function () {
      return _.map(this.dataStoriesTags, (t) => {
        if (this.selectedLanguage === "en") {
          t.label = t.English;
        } else if (this.selectedLanguage === "fr") {
          t.label = t.French;
        } else {
          t.label = t.German;
        }
        return t;
      });
    },
    encodedUrl: function () {
      return window.encodeURIComponent(window.location.href);
    },
    shareEmailUrl: function () {
      return `mailto:?subject=${window.encodeURIComponent(
        "SNF Data Story: ",
      )}${window.encodeURIComponent(
        this.objectAttributeInLanguage(this.story, "Title"),
      )}&body=${window.location.href}`;
    },
    authorsHtml: function () {
      // const names = _.split(this.story.Author, ";");
      const names = _.split(this.objectAttributeInLanguage(this.story, "Author"), ";");
      // const emails = _.split(this.story.AuthorURL, ";");

      let result;
      result = `<h3>${this.cmsTranslationByKey(
        names.length > 1 ? "Authors" : "Author",
      )}</h3>`;
      result += '<ul style="list-style-type: disc;">';

      result += _.map(names, (n) => {
        return `<li style="margin-left: 24px;">${n}</li>`;
      }).join("");

      result += "</ul>";

      return result;
    },
  },
  watch: {
    $route(to, from) {
      log.debug("$route", to, from);
      this.loadPageData();
    },
    selectedLanguage: function (val) {
      log.debug("watch selectedLanguage", val);
      const storyLang = _.split(this.story.Name, ".")[0].slice(-2);
      if (_.includes(this.story.Languages, val) && storyLang !== val) {
        this.loadDataStory(this.story.DataStoryName, val).then(() => {
          this.$router.push({
            name: this.$route.name,
            params: { htmlFilename: this.story.Name },
          });
        });
      }

      this.loadSimilarStories();
    },
  },
  methods: {
    ...mapActions(["loadDataStoriesTags", "setSelectedLanguage"]),
    loadPageData() {
      this.showLoader = true;
      axios
        .get(
          buildEntityQuery(`Name:${this.htmlFilename}`, 1) +
            `&sort=Modified%20desc&fl=${_.join(solrFields, ",")}`,
        )
        .then((response) => {
          if (response.data.response.numFound > 0) {
            this.handleSolrPageData(response);
          } else {
            this.$router.push({ name: "NotFound404WithPath" });
          }
        })
    },

    loadSimilarStories() {
      loadDataStories(
        this.selectedLanguage,
        this.story.tagList,
        3,
        this.story.DataStoryName,
      ).then((stories) => {
        this.similarStories = stories;
      });
    },

    loadDataStory(dataStoryName, language) {
      this.showLoader = true;
      return axios
        .get(
          buildEntityQuery(
            `DataStoryName:${dataStoryName}+AND+Language:${_.upperCase(language)}`,
            1,
          ) + `&sort=Modified%20desc&fl=${_.join(solrFields, ",")}`,
        )
        .then((response) => {
          this.handleSolrPageData(response);
        });
    },

    openSimilarStory(story) {
      this.$router.push({
        name: "DataStory",
        params: {
          htmlFilename: story.Name,
        },
      });
    },

    handleSolrPageData(response) {
      const storyData = response.data.response.docs[0];

      this.story = storyData;
      this.story.tagList = _.split(storyData.Tags, ",");

      this.loadSimilarStories();

      // FIXME: remove testing for DSS-1193
      // if (storyData.FileLink.startsWith("/")) {
      //   storyData.FileLink = "https://data.snf.ch" + storyData.FileLink;
      // }

      axios.get(storyData.FileLink).then((response) => {
        let htmlFileContent = response.data;

        // FIXME: remove testing for DSS-1193
        // htmlFileContent = htmlFileContent.replace(
        //   "https://data.snf.ch",
        //   "http://localhost:8080",
        // );

        htmlFileContent = htmlFileContent.replace(
          "</head>",
          `<style type="text/css">
          :root {
            font-size: 16px;
          }

          body {
            font-family: 'Theinhardt', sans-serif;
            margin: 0 auto;
            padding-bottom: 20px; /* Prevent overlay on the bottom of the iframe */
            max-width: 660px;
            color: #1a1a1a;
          }

          h1 {
            font-size: 48px;
            line-height: 50px;
            font-weight: 700;
            margin-top: 1.5em !important
          }

          h2 {
            font-size: 36px;
            line-height: 40px;
            font-weight: 700;
            margin-top: 1em;
          }

          h3 {
            font-size: 28px;
            line-height: 32px;
            font-weight: 700;
            margin-top: 1em;
          }

          a {
            color: #1a1a1a;
            text-decoration: underline;
          }

          a:hover {
            color: #c95b40;
            transition:
              color .25s ease-out;
              transition-duration: 0.25s;
              transition-timing-function: ease-out;
              transition-delay: 0s;
              transition-property: color;
          }

          p {
            line-height: 1.75;
            font-size: 1.1rem;
            margin: 1em 0 3em 0;
          }

          @media screen and (max-width: 768px) {
            body {
              margin: 0 20px;
            }

            h1 {
              font-size: 36px;
              line-height: 40px;
            }

            h2 {
              font-size: 32px;
              line-height: 36px;
              font-weight: 700;
              margin-top: 1em;
            }

            h3 {
              font-size: 28px;
              line-height: 32px;
              font-weight: 700;
              margin-top: 1em;
            }
          }
        </style></head>`,
        );

        /* eslint-disable no-useless-escape */
        htmlFileContent = htmlFileContent.replace(
          "</body>",
          `<script>
          Array.prototype.slice.call(document.getElementsByTagName('img')).forEach((elem) => {
            elem.addEventListener("click", function() {
              window.parent.showImgFullscreen(elem.getAttribute("src"));
            });
          });
          <\/script>
          </body>`,
        );

        // add "interactive-content" badge to plots in the story
        const interactiveContentText = this.cmsTranslationByKey("InteractiveContent");

        /* eslint-disable no-useless-escape */
        htmlFileContent = htmlFileContent.replace(
            "</body>",
            `<script>
            document.querySelectorAll('.plot-box').forEach(plotBox => {
              // Check if there is a descendant with the class "giraffe"
              if (plotBox.innerHTML.includes('html-widget')) {
                // Create the new element
                // console.log(plotBox.innerHTML);
                // console.log(plotBox);
                const newElement = document.createElement('div');

                // Create the icon element
                const svgIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                svgIcon.setAttribute('width', '18');
                svgIcon.setAttribute('height', '20');
                svgIcon.setAttribute('viewBox', '0 0 18 20');
                svgIcon.setAttribute('fill', 'none');
                svgIcon.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

                // Create the path element
                const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                path.setAttribute('d', 'M7.3374 0C7.50141 0 7.65871 0.0651536 7.77468 0.181128C7.89065 0.297102 7.95581 0.454396 7.95581 0.618408V3.09204C7.95581 3.25605 7.89065 3.41335 7.77468 3.52932C7.65871 3.64529 7.50141 3.71045 7.3374 3.71045C7.17339 3.71045 7.01609 3.64529 6.90012 3.52932C6.78414 3.41335 6.71899 3.25605 6.71899 3.09204V0.618408C6.71899 0.454396 6.78414 0.297102 6.90012 0.181128C7.01609 0.0651536 7.17339 0 7.3374 0ZM2.52619 1.99127C2.58363 1.93368 2.65187 1.88799 2.727 1.85682C2.80213 1.82564 2.88268 1.80959 2.96402 1.80959C3.04536 1.80959 3.1259 1.82564 3.20103 1.85682C3.27616 1.88799 3.34441 1.93368 3.40185 1.99127L5.15071 3.74137C5.20977 3.79841 5.25689 3.86665 5.2893 3.9421C5.32171 4.01755 5.33876 4.0987 5.33948 4.18081C5.34019 4.26292 5.32454 4.34435 5.29345 4.42035C5.26236 4.49635 5.21644 4.5654 5.15837 4.62346C5.10031 4.68153 5.03126 4.72744 4.95526 4.75854C4.87926 4.78963 4.79783 4.80528 4.71572 4.80457C4.63361 4.80385 4.55246 4.78679 4.47701 4.75438C4.40156 4.72197 4.33333 4.67486 4.27628 4.6158L2.52742 2.86694C2.41149 2.75097 2.34636 2.5937 2.34636 2.42972C2.34636 2.26574 2.41149 2.10848 2.52742 1.99251M12.1498 1.99251C12.2074 2.04995 12.2531 2.1182 12.2843 2.19333C12.3155 2.26846 12.3315 2.349 12.3315 2.43034C12.3315 2.51168 12.3155 2.59223 12.2843 2.66736C12.2531 2.74249 12.2074 2.81073 12.1498 2.86818L10.3973 4.6158C10.3402 4.67486 10.272 4.72197 10.1965 4.75438C10.1211 4.78679 10.04 4.80385 9.95784 4.80457C9.87573 4.80528 9.7943 4.78963 9.7183 4.75854C9.6423 4.72744 9.57325 4.68153 9.51519 4.62346C9.45712 4.5654 9.4112 4.49635 9.38011 4.42035C9.34902 4.34435 9.33337 4.26292 9.33408 4.18081C9.3348 4.0987 9.35186 4.01755 9.38427 3.9421C9.41668 3.86665 9.46379 3.79841 9.52285 3.74137L11.2717 1.99251C11.3877 1.87658 11.5449 1.81145 11.7089 1.81145C11.8729 1.81145 12.0302 1.87658 12.1461 1.99251M0.534912 6.80249C0.534912 6.63847 0.600066 6.48118 0.71604 6.36521C0.832014 6.24923 0.989308 6.18408 1.15332 6.18408H3.62695C3.79096 6.18408 3.94826 6.24923 4.06423 6.36521C4.18021 6.48118 4.24536 6.63847 4.24536 6.80249C4.24536 6.9665 4.18021 7.12379 4.06423 7.23977C3.94826 7.35574 3.79096 7.42089 3.62695 7.42089H1.15332C0.989308 7.42089 0.832014 7.35574 0.71604 7.23977C0.600066 7.12379 0.534912 6.9665 0.534912 6.80249ZM8.74737 6.55512C8.56687 6.40478 8.34729 6.30895 8.11432 6.27885C7.88135 6.24874 7.64463 6.2856 7.43184 6.38512C7.21905 6.48463 7.03901 6.64269 6.91277 6.84079C6.78653 7.0389 6.71931 7.26885 6.71899 7.50376V18.76C6.71899 19.9028 8.13515 20.4347 8.88713 19.5751L11.3929 16.7106C11.5672 16.5119 11.782 16.3527 12.0228 16.2437C12.2636 16.1347 12.5249 16.0784 12.7893 16.0786H16.7607C17.9171 16.0786 18.4415 14.6315 17.5523 13.8919L8.74737 6.55512ZM7.95581 18.7625V7.50376L16.7607 14.8418H12.7893C12.3484 14.8417 11.9126 14.9359 11.5112 15.1181C11.1097 15.3003 10.7519 15.5662 10.4616 15.898L7.95581 18.7625Z');
                path.setAttribute('fill', '#04293C');

                // Append the path to the SVG
                svgIcon.appendChild(path);

                // Create the text element
                const text = document.createElement('span');
                text.textContent = '${interactiveContentText}';
                text.style.lineHeight = '18px'; // Ensure text has a consistent height
                text.style.marginTop = '4px'; // Optional for better alignment
                text.classList.add('text'); // Optional class for text styling

                // Append icon and text to the new element container
                newElement.appendChild(svgIcon);
                newElement.appendChild(text);

                // Add any required styling directly or via CSS classes
                newElement.style.display = 'inline-flex';
                newElement.style.gap = '8px';
                newElement.style.alignItems = 'center';
                newElement.style.padding = '5px 10px';
                newElement.style.borderRadius = '20px';
                newElement.style.backgroundColor = '#e3f3ff';
                newElement.style.fontSize = '16px';
                newElement.style.flexShrink = '0';
                newElement.style.alignSelf = 'flex-start';

                // Find the "plot-title" element
                const plotTitle = plotBox.querySelector('.plot-title');

                // Insert the new element after the "plot-title" element
                if (plotTitle) {
                  const wrapper = document.createElement('div');
                  wrapper.style.display = 'flex';
                  wrapper.style.gap = '16px';
                  wrapper.style.alignItems = 'top';
                  wrapper.style.justifyContent = 'space-between'; // Space between plotTitle and newElement

                  // Clone the plotTitle and append it to the wrapper
                  const plotTitleClone = plotTitle.cloneNode(true);
                  wrapper.appendChild(plotTitleClone);
                  wrapper.appendChild(newElement);

                  plotBox.replaceChild(wrapper, plotTitle);
                }
              }
            });
          <\/script>
          </body>`,

        );

        // DSS-1193 open internal links in the same window
        /* eslint-disable no-useless-escape */
        htmlFileContent = htmlFileContent.replace(
          "</body>",
          `<script>
          Array.prototype.slice.call(document.getElementsByTagName('a')).forEach((elem) => {
            elem.addEventListener("click", function(e) {
              const target = e.target;
              const url = target.href;
              if (
                url.startsWith("https://data.snf.ch") ||
                url.startsWith("https://snfcorefigures.iterativ.ch") ||
                url.startsWith("http://localhost:8080")
              ) {
                e.preventDefault(); // Prevent the default link behavior
                parent.location.assign(url); // Go to the URL
              } else {
                // For other URLs, open in a new tab
                target.target = "_blank";
              }
            });
          });
          <\/script>
          </body>`,
        );

        //console.log(htmlFileContent);
        //this.$refs.storyHolder.src = "data:text/html;charset=utf-8," + htmlFileContent;
        const iframeDoc = this.$refs.storyHolder.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(htmlFileContent);
        iframeDoc.close();

        // Timeouts are needed to get correct height of the contentDom
        _.delay(() => {
          this.showLoader = false;
        }, 100);
        _.delay(() => {
          const contentDom = iframeDoc.body;
          const newHeight =
            Math.max(
              contentDom.clientHeight,
              contentDom.scrollHeight,
              contentDom.offsetHeight,
            ) +
            60 +
            "px";
          this.storyFrameStyle.height = newHeight;
        }, 200);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../assets/css/colors";

.data-story {
  hyphens: auto;
  border-top: $snf-gray-light 1px solid;
  margin-bottom: 2em;
}

.data-story-meta {
  margin: 0 16px;
  max-width: 668px;

  @media (min-width: 768px) {
    margin: auto;
  }

  .symbols {
    display: flex;
    margin: 1.5em 0;

    a[class^="symbol-"] {
      width: 26px;
      height: 26px;
      background-repeat: no-repeat;
      background-position: center;
      margin: 4px 8px;

      &:first-child {
        margin-left: 0;
      }
    }

    .symbol-twitter {
      background-image: url("../../public/img/icon-twitter.svg");
    }

    .symbol-facebook {
      background-image: url("../../public/img/icon-facebook.svg");
    }

    .symbol-email {
      background-image: url("../../public/img/icon-email.svg");
    }

    .symbol-github {
      background-image: url("../../public/img/icon-github.svg");
    }

    .symbol-doi {
      background-image: url("../../public/img/icon-doi.svg");
    }
  }

  .tags {
    display: flex;
    flex-wrap: wrap;
  }

  .tag {
    border: $snf-gray-dark 1px solid;
    padding: 6px 12px;
    margin: 10px 10px 10px 0;

    &:hover {
      background-color: $snf-blue-dark;
      color: $snf-white;
    }
  }

  .authors {
    margin: 1.5em 0;
  }

  .similar-stories {
    margin-bottom: 4em;

    h2 {
      font-size: 36px;
      line-height: 40px;
      font-weight: 700;
      margin-top: 1em;
    }

    h3 {
      font-size: 28px;
      line-height: 32px;
      font-weight: 700;
      margin-top: 1em;
    }

    .similar-story {
      margin: 16px 0;

      h3 {
        margin-top: 0;
      }

      hr {
        background-color: $snf-gray-medium;
        border: none;
        display: block;
        height: 1px;
        margin: 32px -16px;
      }
    }

    .story-date {
      margin-top: 16px;
    }

    .story-link {
      margin-top: 2em;
      margin-bottom: 1em;

      a {
        border: $snf-black 1px solid;
        padding: 12px;
      }
    }
  }

  @media (min-width: 768px) {
    .similar-stories {
      &--title {
        margin-bottom: 24px;
      }

      .similar-story {
        padding: 24px;
        margin: 0;
        border-top: $snf-gray-medium 1px solid;
        border-left: $snf-gray-medium 1px solid;
        border-right: $snf-gray-medium 1px solid;

        &:last-child {
          border-bottom: $snf-gray-medium 1px solid;
        }

        transition: transform 0.3s ease;

        &:hover {
          background-color: $snf-gray-light;
          cursor: pointer;
        }

        hr {
          display: none;
        }
      }
    }
  }
}
</style>
